/**
 * Admin Compliance Documents Management Page
 * Manages Terms of Service and HIPAA Authorization documents
 * - View all document versions with signature counts
 * - Create new document versions
 * - Activate documents (triggers user re-consent)
 * - View compliance statistics
 * Access: super_admin, ops_admin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Eye,
  ArrowLeft,
  Loader2,
  X,
  FileText,
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  Users,
  Percent,
  Calendar,
  ChevronDown
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

// Types
interface ConsentDocument {
  id: number;
  document_type: 'terms_of_use' | 'hipaa_authorization';
  version: string;
  version_number: number;
  title: string;
  content: string;
  summary?: string;
  change_description?: string;
  is_active: boolean;
  requires_reconsent: boolean;
  effective_date: string;
  created_at: string;
  created_by?: number;
  published_at?: string;
  published_by?: number;
  signature_count: number;
}

interface ComplianceStats {
  terms_current_version?: string;
  terms_total_users: number;
  terms_signed_users: number;
  terms_pending_users: number;
  terms_compliance_rate: number;
  hipaa_current_version?: string;
  hipaa_total_users: number;
  hipaa_signed_users: number;
  hipaa_pending_users: number;
  hipaa_compliance_rate: number;
}

type ModalMode = 'preview' | 'activate' | null;
type DocumentFilter = 'all' | 'terms_of_use' | 'hipaa_authorization';

export default function ComplianceDocumentsPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<ConsentDocument[]>([]);
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [saving, setSaving] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ConsentDocument | null>(null);
  const [filter, setFilter] = useState<DocumentFilter>('all');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '';
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      'X-CBO-ID': cboId,
    };
  };

  // Load documents and stats
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docsResponse, statsResponse] = await Promise.all([
        fetch(`${getWeaveinApiUrl()}/api/consent/admin/documents`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${getWeaveinApiUrl()}/api/consent/admin/stats`, {
          headers: getAuthHeaders(),
        }),
      ]);

      if (!docsResponse.ok) {
        throw new Error('Failed to load documents');
      }
      if (!statsResponse.ok) {
        throw new Error('Failed to load stats');
      }

      const docsData = await docsResponse.json();
      const statsData = await statsResponse.json();

      setDocuments(docsData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    if (filter === 'all') return true;
    return doc.document_type === filter;
  });

  // Activate document
  const handleActivateDocument = async () => {
    if (!selectedDocument) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(
        `${getWeaveinApiUrl()}/api/consent/admin/documents/${selectedDocument.id}/activate`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to activate document');
      }

      setModalMode(null);
      setSelectedDocument(null);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate document');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = (doc: ConsentDocument) => {
    setSelectedDocument(doc);
    setModalMode('preview');
  };

  const openActivate = (doc: ConsentDocument) => {
    setSelectedDocument(doc);
    setModalMode('activate');
  };

  const getDocumentTypeLabel = (type: string) => {
    return type === 'terms_of_use' ? 'Terms of Service' : 'HIPAA Authorization';
  };

  const getDocumentTypeIcon = (type: string) => {
    return type === 'terms_of_use' ? FileText : Shield;
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Content"
        title="Compliance Documents"
        description="Manage Terms of Service and HIPAA Authorization documents"
        actions={
          <>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={() => navigate('/admin/compliance-documents/editor')}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              New Version
            </button>
          </>
        }
      />
      <PageContainer>

      {/* Stats Cards */}
      {stats && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Terms of Service Stats */}
            <div className="bg-surface rounded-xl p-6 border border-v2border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-ink">Terms of Service</h3>
                  <p className="text-sm text-ink-500">
                    Current: v{stats.terms_current_version || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="font-display text-2xl font-semibold text-green-600">{stats.terms_signed_users}</p>
                  <p className="text-xs text-ink-500">Signed</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold text-yellow-600">{stats.terms_pending_users}</p>
                  <p className="text-xs text-ink-500">Pending</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold text-teal-600">{stats.terms_compliance_rate}%</p>
                  <p className="text-xs text-ink-500">Compliance</p>
                </div>
              </div>
            </div>

            {/* HIPAA Stats */}
            <div className="bg-surface rounded-xl p-6 border border-v2border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-ink">HIPAA Authorization</h3>
                  <p className="text-sm text-ink-500">
                    Current: v{stats.hipaa_current_version || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="font-display text-2xl font-semibold text-green-600">{stats.hipaa_signed_users}</p>
                  <p className="text-xs text-ink-500">Signed</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold text-yellow-600">{stats.hipaa_pending_users}</p>
                  <p className="text-xs text-ink-500">Pending</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold text-teal-600">{stats.hipaa_compliance_rate}%</p>
                  <p className="text-xs text-ink-500">Compliance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filter and Documents Table */}
      <div>
        {/* Filter */}
        <div className="flex items-center gap-4 mb-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as DocumentFilter)}
            className="px-3 py-2 border border-v2border rounded-lg bg-surface text-ink"
          >
            <option value="all">All Document Types</option>
            <option value="terms_of_use">Terms of Service</option>
            <option value="hipaa_authorization">HIPAA Authorization</option>
          </select>
        </div>

        {/* Documents Table */}
        {loading ? (
          <PageState variant="loading" title="Loading documents" />
        ) : filteredDocuments.length === 0 ? (
          <PageState
            variant="empty"
            title="No documents found"
            action={
              <button
                onClick={() => navigate('/admin/compliance-documents/editor')}
                className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
              >
                Create your first document
              </button>
            }
          />
        ) : (
          <div className="bg-surface rounded-xl shadow overflow-hidden border border-v2border">
            <table className="w-full">
              <thead className="bg-paper dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Effective Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Signatures
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {filteredDocuments.map((doc) => {
                  const DocIcon = getDocumentTypeIcon(doc.document_type);
                  return (
                    <tr key={doc.id} className="hover:bg-paper-2/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            doc.document_type === 'terms_of_use'
                              ? 'bg-blue-100 dark:bg-blue-900/30'
                              : 'bg-purple-100 dark:bg-purple-900/30'
                          }`}>
                            <DocIcon className={`w-4 h-4 ${
                              doc.document_type === 'terms_of_use'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-purple-600 dark:text-purple-400'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-ink">{doc.title}</p>
                            <p className="text-xs text-ink-500">
                              {getDocumentTypeLabel(doc.document_type)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-ink">v{doc.version}</span>
                      </td>
                      <td className="px-6 py-4">
                        {doc.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </span>
                        ) : doc.published_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium">
                            <Clock className="w-3 h-3" />
                            Retired
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-paper-2 text-ink-500 rounded-full text-xs font-medium">
                            <Clock className="w-3 h-3" />
                            Draft
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-ink-500">
                          {new Date(doc.effective_date).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-ink-400" />
                          <span className="text-sm text-ink">{doc.signature_count}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openPreview(doc)}
                            className="p-2 hover:bg-paper-2 rounded-lg transition-colors"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4 text-ink-500" />
                          </button>
                          {/* Draft documents: show Edit and Activate */}
                          {!doc.is_active && !doc.published_at && (
                            <>
                              <button
                                onClick={() => navigate(`/admin/compliance-documents/editor?id=${doc.id}`)}
                                className="p-2 hover:bg-paper-2 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4 text-ink-500" />
                              </button>
                              <button
                                onClick={() => openActivate(doc)}
                                className="px-3 py-1.5 bg-teal-500 text-white text-xs font-medium rounded-lg hover:bg-teal-600 transition-colors"
                              >
                                Activate
                              </button>
                            </>
                          )}
                          {/* Retired documents: no Edit or Activate - create new version instead */}
                          {!doc.is_active && doc.published_at && (
                            <span className="text-xs text-ink-400 italic">
                              Create new version to update
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {modalMode === 'preview' && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface p-6 border-b border-v2border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-ink">
                    {selectedDocument.title}
                  </h2>
                  <p className="text-sm text-ink-500">
                    Version {selectedDocument.version} • {getDocumentTypeLabel(selectedDocument.document_type)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setModalMode(null);
                    setSelectedDocument(null);
                  }}
                  className="p-2 hover:bg-paper-2 dark:hover:bg-gray-800 rounded-lg"
                >
                  <X className="w-5 h-5 text-ink-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedDocument.summary && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Summary:</strong> {selectedDocument.summary}
                  </p>
                </div>
              )}
              {selectedDocument.change_description && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    <strong>Changes:</strong> {selectedDocument.change_description}
                  </p>
                </div>
              )}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-ink-700 font-sans bg-paper p-4 rounded-lg overflow-x-auto">
                  {selectedDocument.content}
                </pre>
              </div>
            </div>

            <div className="sticky bottom-0 bg-paper px-6 py-4 border-t border-v2border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-ink-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {selectedDocument.signature_count} signatures
                  </span>
                </div>
                <button
                  onClick={() => {
                    setModalMode(null);
                    setSelectedDocument(null);
                  }}
                  className="px-4 py-2 text-ink-700 border border-v2border rounded-lg hover:bg-paper-2 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activate Confirmation Modal */}
      {modalMode === 'activate' && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-ink">
                    Activate Document
                  </h2>
                  <p className="text-sm text-ink-500">
                    This will require all users to re-consent
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Warning:</strong> Activating "{selectedDocument.title}" (v{selectedDocument.version}) will:
                </p>
                <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
                  <li>Deactivate the current active version</li>
                  <li>Flag all users as needing to re-consent</li>
                  <li>Users will be prompted to accept on their next login</li>
                </ul>
              </div>
            </div>

            <div className="bg-paper px-6 py-4 border-t border-v2border rounded-b-2xl">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setModalMode(null);
                    setSelectedDocument(null);
                  }}
                  className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleActivateDocument}
                  disabled={saving}
                  className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Activate Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </PageContainer>
    </div>
  );
}
